import { useCallback } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { forceRefresh } from "@/lib/force-refresh";

/**
 * Hook that manages SW registration and provides update state.
 * Returns:
 * - needRefresh: true when a new SW is waiting to activate
 * - updateSW: function to activate the new SW and reload
 * - offlineReady: true when the app is ready for offline use
 */
export function usePwaRegistration() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      // Check for updates every 60 minutes
      if (registration) {
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000);
      }
    },
    onRegisterError(error) {
      console.error("SW registration error:", error);
    },
  });

  const close = useCallback(() => {
    setNeedRefresh(false);
    setOfflineReady(false);
  }, [setNeedRefresh, setOfflineReady]);

  return { needRefresh, offlineReady, updateServiceWorker, close };
}

/**
 * Toast-style banner that appears when a new version is available.
 * Renders at the bottom of the screen with "Update" and "Dismiss" buttons.
 */
export function PwaUpdatePrompt() {
  const { needRefresh, offlineReady, updateServiceWorker, close } =
    usePwaRegistration();

  if (!needRefresh && !offlineReady) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 rounded-lg border border-border bg-card p-4 shadow-lg sm:left-auto sm:right-4 sm:w-80">
      {offlineReady && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-foreground">App ready for offline use</p>
          <button
            onClick={close}
            className="ml-4 text-sm font-medium text-paris-red-text hover:text-paris-red-active"
          >
            OK
          </button>
        </div>
      )}
      {needRefresh && (
        <div>
          <p className="text-sm text-foreground">New version available</p>
          <div className="mt-2 flex gap-2">
            <button
              onClick={() => void forceRefresh()}
              className="rounded-md bg-paris-red px-3 py-1.5 text-sm font-medium text-white hover:bg-paris-red-dark"
            >
              Update
            </button>
            <button
              onClick={close}
              className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted"
            >
              Later
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
